/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ISortAndFilterContainerProps } from './sort-and-filter-container.props.autogenerated';
import { GridSize, ISortAndFilterContainerState } from './sort-and-filter-container.state';

/**
 *
 * SortAndFilterContainer component.
 * @extends {React.Component<ISortAndFilterContainerProps<{}>, ISortAndFilterContainerState>}
 */
@observer
class SortAndFilterContainer extends React.Component<ISortAndFilterContainerProps<{}>, ISortAndFilterContainerState> {
    @observable
    private _viewport: GridSize;

    private _modalExampleBtn: React.RefObject<HTMLButtonElement>;

    constructor(props: ISortAndFilterContainerProps<{}>) {
        super(props);

        this._viewport = 'lg';
        this._modalExampleBtn = React.createRef();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);

        this.state = {
            modalIsOpen: false
        };
    }

    @computed get isMobile(): boolean {
        return (this._viewport === 'xs' || this._viewport === 'sm' || this._viewport === 'md');
    }

    public componentDidMount(): void {
        // tslint:disable-next-line: no-typeof-undefined
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
    }

    public componentWillUnmount(): void {
        // tslint:disable-next-line: no-typeof-undefined
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    /**
     * Renders both desktop and mobile versions of this container. The expectation is for the theme
     * to show/hide each based on viewport size.
     */
    public render(): JSX.Element {
        if (this.isMobile) {
            return this._renderMobile();
        }
        return this._renderDesktop();
    }

    private _renderDesktop(): JSX.Element {
        const { slots } = this.props;

        return (
            <div className={classname('sort_and_filter_desktop_view container', this.props.config.className)}>
                <div className='row'>
                    <div className='col-lg-3'>
                        {slots.refineMenu}
                    </div>
                    <div className='col-lg-9'>
                        <div className='sort-and-filter-desktop__choices-and-sorting row'>
                            <div className='sort-and-filter-desktop__choices col-9'>
                                {slots.choiceSummary}
                            </div>
                            <div className='sort-and-filter-desktop__sorting col-3'>
                                {slots.sorting}
                            </div>
                        </div>
                        <div className='sort-and-filter-desktop__products row'>
                            <div className='col-12'>
                                {slots.products}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private _renderMobile(): JSX.Element {
        const { slots, resources } = this.props;
        const { modalIsOpen } = this.state;

        return (
            <div className={classname('sort_and_filter_mobile_view container', this.props.config.className)}>
                <div className='breadcrumb-placeholder row' />
                <div className='results-title-placeholder row' />
                <div className='choice-summary-container row'>
                    <div className='col-12'>
                        {slots.choiceSummary}
                    </div>
                </div>
                <div className='sort-and-filter-container row'>
                    <div className='category-dropdown-placeholder row' />
                    <div className='sort-and-filter-modal-container row'>
                        <div className='col-12'>
                            <Button
                                innerRef={this._modalExampleBtn}
                                className='sort-and-filter-modal-toggle'
                                onClick={this._toggleModal}
                                color='secondary'
                            >
                                <i className='sort-and-filter-button-icon' />
                                <span className='sort-and-filter-modal-button-title'>{resources.mobileModalTitle}</span>
                            </Button>
                            <Modal
                                autoFocus={true}
                                fade={false}
                                applicationNode={'renderPage'}
                                returnFocusRef={this._modalExampleBtn}
                                isOpen={modalIsOpen}
                                toggle={this._toggleModal}
                                horizontalPosition={'right'}
                                verticalPosition={'top'}
                                modalClassName={'sort-and-filter-modal'}
                                zIndex={1050}
                            >
                                <ModalHeader toggle={this._toggleModal}>{resources.mobileModalTitle}</ModalHeader>
                                <ModalBody>
                                    <div className='sort-and-filter-modal-sorting'>{slots.sorting}</div>
                                    <hr className='sort-and-filter-modal-sort-refine-menu-divider' />
                                    <div className='sort-and-filter-modal-refine-menu'>{slots.refineMenu}</div>
                                </ModalBody>
                                <ModalFooter>
                                    <Button color='primary' onClick={this._toggleModal} className='sort-and-filter-modal-close'>
                                        {resources.mobileModalCloseButtonText}
                                    </Button>
                                </ModalFooter>
                            </Modal>
                        </div>
                    </div>
                </div>
                <div className='products-container row'>
                    <div className='col-12'>
                        {slots.products}
                    </div>
                </div>
            </div>
        );
    }

    private _toggleModal(): void {
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.isMobile && this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    // TODO: if DCR 22025838 is approved then move this logic to SDK and reuse across Starter Kit modules
    private _getViewport(): GridSize {
        const { context } = this.props;

        // always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (typeof window !== undefined && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                } else {
                    return 'xl';
                }
            }
        }

        return 'xs';
    }
}

export default SortAndFilterContainer;
